import styled from "@emotion/styled";
import { Typography } from "@avenue-8/sidekick-ui";
import { BulletSeparator } from "@shared/components/BulletSeparator";
import { RadarMonitoringHeaderToggle } from "@features/Contacts/components/RadarMonitoringHeaderToggle";

export type MonitoringSlots =
  | { status: "loading" }
  | { status: "not-set-up" }
  | { status: "unavailable" }
  | { status: "ready"; remaining: number };

type MonitorAllOptionProps = {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled: boolean;
  contactsCount: number;
  slots: MonitoringSlots;
};

const UNAVAILABLE_DESCRIPTIONS: Record<
  Exclude<MonitoringSlots["status"], "ready">,
  string
> = {
  loading: "Checking your monitoring slots...",
  "not-set-up": "Turn on Radar for a contact first",
  unavailable: "Couldn't load your monitoring slots",
};

const pluralize = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

export const MonitorAllOption = ({
  checked,
  onChange,
  disabled,
  contactsCount,
  slots,
}: MonitorAllOptionProps) => {
  const remainingSlots = slots.status === "ready" ? slots.remaining : 0;
  const canMonitor = remainingSlots > 0;
  const exceedsRemaining =
    checked && canMonitor && contactsCount > remainingSlots;
  const slotsLabel = pluralize(
    remainingSlots,
    "monitoring slot",
    "monitoring slots",
  );
  const description =
    slots.status !== "ready" ? (
      UNAVAILABLE_DESCRIPTIONS[slots.status]
    ) : canMonitor ? (
      <>
        Life & career updates
        <BulletSeparator />
        {slotsLabel} left
      </>
    ) : (
      "No monitoring slots left"
    );

  return (
    <Container>
      <RadarMonitoringHeaderToggle
        isMonitored={checked && canMonitor}
        isMonitoredSince={null}
        isTogglePending={disabled || !canMonitor}
        onToggle={onChange}
        title="Monitor all imported contacts"
        description={description}
      />
      {exceedsRemaining && (
        <WarningText variant="caption">
          You have {slotsLabel} left, so only the first {remainingSlots}{" "}
          contacts in the list will be monitored. The other{" "}
          {contactsCount - remainingSlots} will be imported without monitoring.
        </WarningText>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.25rem;
`;

const WarningText = styled(Typography)`
  color: ${(p) => p.theme.colors.warning};
`;
